import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import type {
  ClassId,
  ClassScheduleCustomerData,
  ClassScheduleId,
  CustomerId,
} from '@gh/common/models'
import {
  CLASS_NOTIFICATION_INTERVALS,
  CLASS_REMINDER_GLOBALS_FIELD,
  DEFAULT_CLASS_REMINDER_GLOBALS,
  DEFAULT_SCHEDULE_NOTIFICATION_CONFIG,
  enrollmentNotificationConfigFromGlobalSms,
  effectiveEnrollmentIntervalsFcm,
  effectiveEnrollmentIntervalsSms,
  parseClassScheduleNotificationConfig,
  type ClassNotificationInterval,
  type ClassScheduleNotificationConfig,
} from '@gh/common/models'
import { firestore } from '@/firebase'
import { useCustomerStore } from './customer'
import { useFirebaseMessagingStore } from './firebaseMessaging'
import { useClassesStore } from './classes'
import { chunkArrayForFirestore } from '@/utils/firestoreUtils'

function mergeEnrollmentNotificationConfig(raw: unknown): ClassScheduleNotificationConfig {
  return parseClassScheduleNotificationConfig(raw)
}

const INTERVAL_ORDER: ClassNotificationInterval[] = ['1h', '30m', '15m']

/** New per-channel reminder lead when the customer enables FCM or SMS with no interval yet. */
const DEFAULT_CHANNEL_REMINDER_LEAD: ClassNotificationInterval = '15m'

const ALL_INTERVALS_SET = new Set<ClassNotificationInterval>(CLASS_NOTIFICATION_INTERVALS)

export type ClassNotificationChannel = 'fcm' | 'sms'

function sortIntervals(list: ClassNotificationInterval[]): ClassNotificationInterval[] {
  return [...list].sort((a, b) => INTERVAL_ORDER.indexOf(a) - INTERVAL_ORDER.indexOf(b))
}

export const useCustomerClassSchedulesStore = defineStore('customerClassSchedules', () => {
  const customerStore = useCustomerStore()
  const classesStore = useClassesStore()
  const firebaseMessagingStore = useFirebaseMessagingStore()

  const enrolledClassScheduleIds = ref<Set<ClassScheduleId>>(new Set())
  const enrolledClassIds = ref<Set<ClassId>>(new Set())
  const adminUpdatedSchedules = ref<{ scheduleId: ClassScheduleId; customerId: CustomerId }[]>([])
  const migrationAddedSchedules = ref<{ scheduleId: ClassScheduleId; customerId: CustomerId }[]>([])
  const signupAddedSchedules = ref<{ scheduleId: ClassScheduleId; customerId: CustomerId }[]>([])
  const showOnlyMySchedule = ref(true)
  const enrollmentNotificationConfigByScheduleId = ref<
    Map<ClassScheduleId, ClassScheduleNotificationConfig>
  >(new Map())
  const isScheduleEnrollmentLoading = ref(false)
  const isEnrolledClassIdsLoading = ref(false)
  const enrolledClassIdsLoadRequestId = ref(0)
  const isLoading = computed(
    () => isScheduleEnrollmentLoading.value || isEnrolledClassIdsLoading.value,
  )

  type CalendarClassFilter = 'all' | 'enrolled' | 'mySchedule'
  const calendarClassFilter = ref<CalendarClassFilter>('all')

  function toggleShowOnlyMySchedule() {
    showOnlyMySchedule.value = !showOnlyMySchedule.value
  }

  function setCalendarClassFilter(filter: CalendarClassFilter) {
    calendarClassFilter.value = filter
  }
  let unsubscribeCustomerClassSchedulesSnapshot: (() => void) | null = null

  // Always work with the *value* of the computed customerId from the customer store
  const customerId = computed(() => customerStore.customerId)

  const subscribeToCustomerClassSchedules = () => {
    const id = customerId.value
    if (unsubscribeCustomerClassSchedulesSnapshot) {
      unsubscribeCustomerClassSchedulesSnapshot()
    }

    if (!id) {
      return
    }

    isScheduleEnrollmentLoading.value = true
    const q = query(collection(firestore, 'customers', id, 'customer_class_schedule_enrollment'))

    unsubscribeCustomerClassSchedulesSnapshot = onSnapshot(
      q,
      (snapshot) => {
        const next = new Set<ClassScheduleId>()
        const nextEnrollmentMap = new Map<ClassScheduleId, ClassScheduleNotificationConfig>()
        const nextAdminUpdated: { scheduleId: ClassScheduleId; customerId: CustomerId }[] = []
        const nextMigrationAdded: { scheduleId: ClassScheduleId; customerId: CustomerId }[] = []
        const nextSignupAdded: { scheduleId: ClassScheduleId; customerId: CustomerId }[] = []

        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as ClassScheduleCustomerData & {
            addedBy?: 'admin' | 'customer' | 'migration' | 'signup'
            dismissed?: boolean
          }

          const scheduleId = docSnap.id as ClassScheduleId

          if (data.addedBy === 'admin' && data.dismissed !== true) {
            nextAdminUpdated.push({
              scheduleId,
              customerId: data.customerId,
            })
          }

          if (data.addedBy === 'migration' && data.dismissed !== true) {
            nextMigrationAdded.push({
              scheduleId,
              customerId: data.customerId,
            })
          }
          if (data.addedBy === 'signup' && data.dismissed !== true) {
            nextSignupAdded.push({
              scheduleId,
              customerId: data.customerId,
            })
          }

          if (data.enrolled) {
            next.add(scheduleId)
            nextEnrollmentMap.set(
              scheduleId,
              mergeEnrollmentNotificationConfig(data.notificationConfig),
            )
          }
        })
        enrolledClassScheduleIds.value = next
        enrollmentNotificationConfigByScheduleId.value = nextEnrollmentMap
        adminUpdatedSchedules.value = nextAdminUpdated
        migrationAddedSchedules.value = nextMigrationAdded
        signupAddedSchedules.value = nextSignupAdded
        isScheduleEnrollmentLoading.value = false
      },
      (error) => {
        console.error('error subscribing to customer class schedules snapshot', error)
        isScheduleEnrollmentLoading.value = false
      },
    )
  }

  watch(
    customerId,
    (newCustomerId) => {
      if (unsubscribeCustomerClassSchedulesSnapshot) {
        unsubscribeCustomerClassSchedulesSnapshot()
        unsubscribeCustomerClassSchedulesSnapshot = null
      }

      // Clear customer-scoped state immediately to avoid showing stale data after account switches.
      enrolledClassScheduleIds.value = new Set()
      enrolledClassIds.value = new Set()
      enrollmentNotificationConfigByScheduleId.value = new Map()
      adminUpdatedSchedules.value = []
      migrationAddedSchedules.value = []
      signupAddedSchedules.value = []
      isScheduleEnrollmentLoading.value = false
      isEnrolledClassIdsLoading.value = false

      if (newCustomerId) {
        subscribeToCustomerClassSchedules()
      }
    },
    { immediate: true },
  )

  const loadEnrolledClassIdsForSchedules = async (scheduleIds: Set<ClassScheduleId>) => {
    const requestId = ++enrolledClassIdsLoadRequestId.value
    const ids = Array.from(scheduleIds)

    if (ids.length === 0) {
      if (requestId === enrolledClassIdsLoadRequestId.value) {
        enrolledClassIds.value = new Set()
        isEnrolledClassIdsLoading.value = false
      }
      return
    }

    // isEnrolledClassIdsLoading.value = true
    try {
      const classIds = new Set<ClassId>()
      const scheduleIdChunks = chunkArrayForFirestore(ids)
      const scheduleChunks = await Promise.all(
        scheduleIdChunks.map((idsChunk) =>
          getDocs(
            query(collection(firestore, 'class_schedules'), where(documentId(), 'in', idsChunk)),
          ),
        ),
      )

      scheduleChunks.forEach((scheduleSnapshot) => {
        scheduleSnapshot.forEach((scheduleDoc) => {
          const classId = scheduleDoc.data().classId
          if (typeof classId === 'string') {
            classIds.add(classId as ClassId)
          }
        })
      })

      if (requestId === enrolledClassIdsLoadRequestId.value) {
        enrolledClassIds.value = classIds
        isEnrolledClassIdsLoading.value = false
      }
    } catch {
      if (requestId === enrolledClassIdsLoadRequestId.value) {
        enrolledClassIds.value = new Set()
        isEnrolledClassIdsLoading.value = false
      }
    }
  }

  watch(
    enrolledClassScheduleIds,
    (scheduleIds) => {
      void loadEnrolledClassIdsForSchedules(scheduleIds)
    },
    { immediate: true },
  )

  const addClassScheduleToCustomerSchedule = async (
    _classScheduleId: ClassScheduleId,
    _classId: ClassId,
  ) => {
    const currentCustomerId = customerId.value
    if (!currentCustomerId || !_classScheduleId) {
      console.error('addClassScheduleToCustomerSchedule: Invalid customer ID or class schedule ID')
      return
    }

    const docRef = doc(
      firestore,
      'customers',
      currentCustomerId,
      'customer_class_schedule_enrollment',
      _classScheduleId,
    )

    const docData = {
      id: _classScheduleId,
      customerId: currentCustomerId,
      addedBy: 'customer',
      dismissed: true,
      enrolled: true,
      notificationConfig: enrollmentNotificationConfigFromGlobalSms(
        firebaseMessagingStore.classReminderGlobalSms,
      ),
    } as ClassScheduleCustomerData

    await setDoc(docRef, docData, { merge: true })
    await customerStore.addClassToFavorites(_classId)
  }

  const removeClassScheduleFromCustomerSchedule = async (_classScheduleId: ClassScheduleId) => {
    const currentCustomerId = customerId.value
    if (!currentCustomerId || !_classScheduleId) {
      console.error(
        'removeClassScheduleFromCustomerSchedule: Invalid customer ID or class schedule ID',
      )
      return
    }

    const docRef = doc(
      firestore,
      'customers',
      currentCustomerId,
      'customer_class_schedule_enrollment',
      _classScheduleId,
    )

    const docData = {
      id: _classScheduleId,
      customerId: currentCustomerId,
      enrolled: false,
      addedBy: 'customer',
      dismissed: true,
    } as ClassScheduleCustomerData
    await setDoc(docRef, docData, { merge: true })
  }

  const isClassScheduleInCustomerSchedule = (_classScheduleId: ClassScheduleId | null) => {
    return _classScheduleId !== null && enrolledClassScheduleIds.value.has(_classScheduleId)
  }

  const removeAllSchedulesForClass = async (_classId: ClassId) => {
    const currentCustomerId = customerId.value
    if (!currentCustomerId || !_classId) {
      return
    }

    const classItem = classesStore.classesMap.get(_classId)
    if (!classItem) {
      console.error('removeAllSchedulesForClass: Invalid class item')
      return
    }
    const classItemWithSchedules = classItem as typeof classItem & {
      schedules?: { id: ClassScheduleId }[]
    }
    if (
      !Array.isArray(classItemWithSchedules.schedules) ||
      !classItemWithSchedules.schedules.length
    ) {
      console.error('removeAllSchedulesForClass: Invalid class item')
      return
    }
    const scheduleIdsForClass = classItemWithSchedules.schedules
      .map((schedule) => schedule.id as ClassScheduleId)
      .filter((id) => !!id && enrolledClassScheduleIds.value.has(id))

    if (!scheduleIdsForClass.length) {
      return
    }

    await Promise.all(scheduleIdsForClass.map((id) => removeClassScheduleFromCustomerSchedule(id)))
  }

  const getEnrollmentNotificationConfig = (scheduleId: ClassScheduleId) => {
    return (
      enrollmentNotificationConfigByScheduleId.value.get(scheduleId) ??
      mergeEnrollmentNotificationConfig(undefined)
    )
  }

  const isScheduleChannelAllowed = (
    _scheduleId: ClassScheduleId,
    channel: ClassNotificationChannel,
  ) => {
    if (channel === 'sms') {
      if (!firebaseMessagingStore.classReminderGlobalSms) {
        return false
      }
      return true
    }
    if (!firebaseMessagingStore.classReminderGlobalFcm) {
      return false
    }
    return true
  }

  const isChannelReminderOn = (scheduleId: ClassScheduleId, channel: ClassNotificationChannel) => {
    const c = getEnrollmentNotificationConfig(scheduleId)
    if (channel === 'fcm') {
      return !!(c.fcm.enabled && effectiveEnrollmentIntervalsFcm(c).length > 0)
    }
    return !!(c.sms.enabled && effectiveEnrollmentIntervalsSms(c).length > 0)
  }

  /** True when FCM (push) reminders are enabled for this enrollment (used by calendar / class UI). */
  const isUpcomingReminderEnabledForSchedule = (scheduleId: ClassScheduleId) => {
    return isChannelReminderOn(scheduleId, 'fcm')
  }

  const isUpcomingReminderEnabledForClass = (classId: ClassId) => {
    const classItem = classesStore.classesMap.get(classId) as
      | { schedules?: { id: ClassScheduleId }[] }
      | undefined
    const schedules = classItem?.schedules ?? []
    return schedules.some(
      (s) =>
        s.id &&
        enrolledClassScheduleIds.value.has(s.id as ClassScheduleId) &&
        isUpcomingReminderEnabledForSchedule(s.id as ClassScheduleId),
    )
  }

  const enrolledScheduleIdsForClass = (classId: ClassId): ClassScheduleId[] => {
    const classItem = classesStore.classesMap.get(classId) as
      | { schedules?: { id: ClassScheduleId }[] }
      | undefined
    const schedules = classItem?.schedules ?? []
    return schedules
      .map((s) => s.id)
      .filter(
        (id): id is ClassScheduleId =>
          !!id && enrolledClassScheduleIds.value.has(id as ClassScheduleId),
      ) as ClassScheduleId[]
  }

  const writeEnrollmentNotificationConfig = async (
    scheduleId: ClassScheduleId,
    cfg: ClassScheduleNotificationConfig,
  ) => {
    const cid = customerId.value
    if (!cid) {
      return
    }
    await updateDoc(
      doc(firestore, 'customers', cid, 'customer_class_schedule_enrollment', scheduleId),
      { notificationConfig: cfg },
    )
  }

  const setScheduleChannelEnabled = async (
    scheduleId: ClassScheduleId,
    channel: ClassNotificationChannel,
    enabled: boolean,
  ) => {
    if (!enrolledClassScheduleIds.value.has(scheduleId)) {
      return
    }
    if (enabled && !isScheduleChannelAllowed(scheduleId, channel)) {
      return
    }
    const prev = getEnrollmentNotificationConfig(scheduleId)

    let next: ClassScheduleNotificationConfig = {
      fcm: { ...prev.fcm },
      sms: { ...prev.sms },
    }

    if (enabled) {
      const ch = channel === 'fcm' ? prev.fcm : prev.sms
      next = {
        ...next,
        [channel]: {
          enabled: true,
          intervals: ch.intervals.length > 0 ? [...ch.intervals] : [DEFAULT_CHANNEL_REMINDER_LEAD],
        },
      }
    } else {
      next = {
        ...next,
        [channel]: {
          enabled: false,
          intervals: [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG[channel].intervals],
        },
      }
    }

    await writeEnrollmentNotificationConfig(scheduleId, parseClassScheduleNotificationConfig(next))
  }

  /** @deprecated Prefer {@link setScheduleChannelEnabled} with channel `'fcm'`. */
  const setScheduleUpcomingPushEnabled = async (scheduleId: ClassScheduleId, enabled: boolean) => {
    await setScheduleChannelEnabled(scheduleId, 'fcm', enabled)
  }

  const setClassUpcomingPushEnabled = async (classId: ClassId, enabled: boolean) => {
    const ids = enrolledScheduleIdsForClass(classId)
    await Promise.all(ids.map((sid) => setScheduleUpcomingPushEnabled(sid, enabled)))
  }

  /** @deprecated Prefer {@link setScheduleChannelEnabled} or {@link setEnrollmentIntervalEnabled} */
  const updateScheduleNotificationConfig = async (
    scheduleId: ClassScheduleId,
    patch: Partial<ClassScheduleNotificationConfig>,
  ) => {
    const prev = getEnrollmentNotificationConfig(scheduleId)
    const merged: ClassScheduleNotificationConfig = {
      fcm: patch.fcm ? { ...prev.fcm, ...patch.fcm } : prev.fcm,
      sms: patch.sms ? { ...prev.sms, ...patch.sms } : prev.sms,
    }
    await writeEnrollmentNotificationConfig(
      scheduleId,
      parseClassScheduleNotificationConfig(merged),
    )
  }

  const setEnrollmentIntervalEnabled = async (
    scheduleId: ClassScheduleId,
    interval: ClassNotificationInterval,
    enabled: boolean,
  ) => {
    const prev = getEnrollmentNotificationConfig(scheduleId)
    const nextSet = new Set<ClassNotificationInterval>(
      [...prev.fcm.intervals, ...prev.sms.intervals].filter((i) => ALL_INTERVALS_SET.has(i)),
    )
    if (enabled) {
      nextSet.add(interval)
    } else {
      nextSet.delete(interval)
    }
    const nextIntervals = sortIntervals([...nextSet])
    if (nextIntervals.length === 0) {
      const next = parseClassScheduleNotificationConfig({
        fcm: { enabled: false, intervals: [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.fcm.intervals] },
        sms: { enabled: false, intervals: [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.sms.intervals] },
      })
      await writeEnrollmentNotificationConfig(scheduleId, next)
      return
    }
    const next = parseClassScheduleNotificationConfig({
      fcm: { ...prev.fcm, intervals: nextIntervals },
      sms: { ...prev.sms, intervals: nextIntervals },
    })
    await writeEnrollmentNotificationConfig(scheduleId, next)
  }

  const toggleScheduleInterval = setEnrollmentIntervalEnabled

  const setAllEnrollmentsSmsEnabled = async (
    channel: ClassNotificationChannel,
    enabled: boolean,
  ) => {
    const ids = [...enrolledClassScheduleIds.value]
    await Promise.all(ids.map((sid) => setScheduleChannelEnabled(sid, channel, enabled)))
    void loadEnrolledClassIdsForSchedules(enrolledClassScheduleIds.value)
  }

  /** Global class-reminder opt-in for FCM or SMS (`customer_notification_configuration.classReminderGlobals`). Cascades channel on/off to all enrollments (on: 15m lead per schedule). */
  const setGlobalClassReminderChannel = async (
    channel: ClassNotificationChannel,
    enabled: boolean,
  ) => {
    const cid = customerId.value
    if (!cid) {
      return
    }

    if (channel === 'fcm' && enabled) {
      await firebaseMessagingStore.requestPermission()
      if (firebaseMessagingStore.permission !== 'granted') {
        return
      }
    }

    const notifConfigRef = doc(firestore, 'customer_notification_configuration', cid)
    if (!(await getDoc(notifConfigRef)).exists()) {
      await setDoc(notifConfigRef, {
        tokens: {},
        [CLASS_REMINDER_GLOBALS_FIELD]: { ...DEFAULT_CLASS_REMINDER_GLOBALS },
      })
    }
    await updateDoc(notifConfigRef, {
      [`${CLASS_REMINDER_GLOBALS_FIELD}.${channel}`]: enabled,
    })

    const ids = [...enrolledClassScheduleIds.value]
    if (!enabled) {
      await Promise.all(ids.map((sid) => setScheduleChannelEnabled(sid, channel, false)))
    } else {
      await Promise.all(
        ids.map((sid) =>
          setEnrollmentReminderLeadForChannel(sid, channel, DEFAULT_CHANNEL_REMINDER_LEAD),
        ),
      )
    }
    void loadEnrolledClassIdsForSchedules(enrolledClassScheduleIds.value)
  }

  /** Updates only `classReminderGlobals.smsDaily` (daily digest); does not change per-schedule SMS enrollment. */
  const setGlobalClassReminderSmsDaily = async (enabled: boolean) => {
    const cid = customerId.value
    if (!cid) {
      return
    }

    if (firebaseMessagingStore.customerIoSmsOptedOut) {
      return
    }

    const notifConfigRef = doc(firestore, 'customer_notification_configuration', cid)
    if (!(await getDoc(notifConfigRef)).exists()) {
      await setDoc(notifConfigRef, {
        tokens: {},
        [CLASS_REMINDER_GLOBALS_FIELD]: { ...DEFAULT_CLASS_REMINDER_GLOBALS },
      })
    }
    await updateDoc(notifConfigRef, {
      [`${CLASS_REMINDER_GLOBALS_FIELD}.smsDaily`]: enabled,
    })
  }

  const getEnrollmentReminderLeadForChannel = (
    scheduleId: ClassScheduleId,
    channel: ClassNotificationChannel,
  ): ClassNotificationInterval | null => {
    const cfg = getEnrollmentNotificationConfig(scheduleId)
    const eff =
      channel === 'fcm'
        ? effectiveEnrollmentIntervalsFcm(cfg)
        : effectiveEnrollmentIntervalsSms(cfg)
    const sorted = sortIntervals([...eff])
    return sorted[0] ?? null
  }

  /** @deprecated Use {@link getEnrollmentReminderLeadForChannel} with a channel. */
  const getEnrollmentReminderLeadForRow = (
    scheduleId: ClassScheduleId,
  ): ClassNotificationInterval | null => {
    return getEnrollmentReminderLeadForChannel(scheduleId, 'fcm')
  }

  const setEnrollmentReminderLeadForChannel = async (
    scheduleId: ClassScheduleId,
    channel: ClassNotificationChannel,
    interval: ClassNotificationInterval | null,
  ) => {
    if (!enrolledClassScheduleIds.value.has(scheduleId)) {
      return
    }
    const prev = getEnrollmentNotificationConfig(scheduleId)

    if (interval === null) {
      const next: ClassScheduleNotificationConfig = {
        ...prev,
        [channel]: {
          enabled: false,
          intervals: [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG[channel].intervals],
        },
      }
      await writeEnrollmentNotificationConfig(
        scheduleId,
        parseClassScheduleNotificationConfig(next),
      )
      return
    }

    const sorted = sortIntervals([interval])
    const next: ClassScheduleNotificationConfig = {
      ...prev,
      [channel]: {
        enabled: true,
        intervals: sorted,
      },
    }
    await writeEnrollmentNotificationConfig(scheduleId, parseClassScheduleNotificationConfig(next))
  }

  /** @deprecated Use {@link setEnrollmentReminderLeadForChannel}. */
  const setEnrollmentReminderLead = async (
    scheduleId: ClassScheduleId,
    interval: ClassNotificationInterval | null,
  ) => {
    if (!enrolledClassScheduleIds.value.has(scheduleId)) {
      return
    }
    const prev = getEnrollmentNotificationConfig(scheduleId)
    if (interval === null) {
      const next = parseClassScheduleNotificationConfig({
        fcm: { enabled: false, intervals: [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.fcm.intervals] },
        sms: { enabled: false, intervals: [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.sms.intervals] },
      })
      await writeEnrollmentNotificationConfig(scheduleId, next)
      return
    }
    const sorted = sortIntervals([interval])
    const next = parseClassScheduleNotificationConfig({
      fcm: { ...prev.fcm, intervals: sorted },
      sms: { ...prev.sms, intervals: sorted },
    })
    await writeEnrollmentNotificationConfig(scheduleId, next)
  }

  const dismissScheduleChangedBannerForCustomer = async (
    items: { scheduleId: ClassScheduleId; customerId: CustomerId }[],
  ) => {
    const currentCustomerId = customerId.value
    if (!currentCustomerId) {
      return
    }

    if (!items.length) {
      return
    }

    await Promise.all(
      items.map(({ scheduleId, customerId: targetCustomerId }) =>
        setDoc(
          doc(
            firestore,
            'customers',
            targetCustomerId,
            'customer_class_schedule_enrollment',
            scheduleId,
          ),
          { dismissed: true },
          { merge: true },
        ),
      ),
    )
  }

  const dismissAdminUpdatedSchedulesForCustomer = async () => {
    await dismissScheduleChangedBannerForCustomer(adminUpdatedSchedules.value)
  }

  const dismissMigrationAddedSchedulesForCustomer = async () => {
    await dismissScheduleChangedBannerForCustomer(migrationAddedSchedules.value)
  }

  const dismissSignupAddedSchedulesForCustomer = async () => {
    await dismissScheduleChangedBannerForCustomer(signupAddedSchedules.value)
  }

  return {
    addClassScheduleToCustomerSchedule,
    removeClassScheduleFromCustomerSchedule,
    isClassScheduleInCustomerSchedule,
    removeAllSchedulesForClass,
    enrolledClassScheduleIds,
    enrolledClassIds,
    adminUpdatedSchedules,
    migrationAddedSchedules,
    signupAddedSchedules,
    showOnlyMySchedule,
    toggleShowOnlyMySchedule,
    calendarClassFilter,
    setCalendarClassFilter,
    dismissAdminUpdatedSchedulesForCustomer,
    dismissMigrationAddedSchedulesForCustomer,
    dismissSignupAddedSchedulesForCustomer,
    isLoading,
    enrollmentNotificationConfigByScheduleId,
    getEnrollmentNotificationConfig,
    isScheduleChannelAllowed,
    isChannelReminderOn,
    isUpcomingReminderEnabledForSchedule,
    isUpcomingReminderEnabledForClass,
    enrolledScheduleIdsForClass,
    setClassUpcomingPushEnabled,
    setScheduleChannelEnabled,
    setScheduleUpcomingPushEnabled,
    setEnrollmentIntervalEnabled,
    toggleScheduleInterval,
    setAllEnrollmentsSmsEnabled,
    setGlobalClassReminderChannel,
    setGlobalClassReminderSmsDaily,
    getEnrollmentReminderLeadForChannel,
    getEnrollmentReminderLeadForRow,
    setEnrollmentReminderLeadForChannel,
    setEnrollmentReminderLead,
    updateScheduleNotificationConfig,
  }
})
